/** Automation Engine — domain types (Sprint 1.3) */

export type AutomationJobStatus =
  | 'QUEUED'
  | 'RUNNING'
  | 'COMPLETED'
  | 'FAILED'
  | 'CANCELLED'
  | 'RETRYING'

export type AutomationSessionStatus = 'ACTIVE' | 'EXPIRED' | 'CLOSED'

export type AutomationOperation =
  | 'initialize'
  | 'authenticate'
  | 'open'
  | 'navigate'
  | 'search'
  | 'extract'
  | 'download'
  | 'close'
  | 'healthCheck'

export type AutomationPriority = 1 | 2 | 3 | 4 | 5

export type AutomationLogLevel = 'debug' | 'info' | 'warn' | 'error'

export type AutomationEventType =
  | 'AutomationStarted'
  | 'AutomationFinished'
  | 'AutomationFailed'
  | 'SessionExpired'

export interface AutomationTask {
  id: string
  supplier: string
  operation: AutomationOperation | string
  status: AutomationJobStatus
  priority: AutomationPriority
  payload?: Record<string, unknown>
  createdAt: string
  startedAt: string | null
  finishedAt: string | null
  duration: number | null
  error: string | null
  attempts: number
  maxAttempts: number
}

export interface AutomationResult {
  success: boolean
  executionTime: number
  itemsFound: number
  warnings: string[]
  errors: string[]
  logs: string[]
  data?: unknown
}

export interface AutomationJob {
  id: string
  supplierId: string
  type: string
  status: AutomationJobStatus
  priority: AutomationPriority
  attempts: number
  startedAt: string | null
  finishedAt: string | null
  executionTime: number | null
  errorMessage: string | null
  createdAt: string
  updatedAt: string
}

export interface AutomationSession {
  id: string
  supplierId: string
  status: AutomationSessionStatus
  createdAt: string
  expiresAt: string
  lastActivity: string
}

export interface AutomationLog {
  id: string
  jobId: string
  level: AutomationLogLevel
  message: string
  createdAt: string
}

export interface AutomationConfig {
  maxWorkers: number
  timeoutMs: number
  maxAttempts: number
  sessionTtlMs: number
  retryBaseDelayMs: number
}

export interface AutomationDashboard {
  queued: number
  running: number
  completed: number
  failed: number
  averageDuration: number
}

export interface AutomationEvent {
  id: string
  type: AutomationEventType
  supplierId?: string
  jobId?: string
  sessionId?: string
  message: string
  createdAt: string
}

export interface AutomationFilters {
  supplierId?: string
  status?: AutomationJobStatus | 'ALL' | null
  periodFrom?: string | null
  periodTo?: string | null
}

export const DEFAULT_AUTOMATION_CONFIG: AutomationConfig = {
  maxWorkers: 3,
  timeoutMs: 15_000,
  maxAttempts: 3,
  sessionTtlMs: 30 * 60_000,
  retryBaseDelayMs: 50,
}
